import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation, useNavigate } from 'react-router-dom';
import { COUNTRY_OPTIONS } from '../data/countries';
// @ts-ignore
import { createRule, updateRule, getRule } from '../graphql/index';
import SuccessNotification from './SuccessNotification';
import ErrorNotification from './ErrorNotification';
import { combineDateTimeToUTC } from '../utils/dateTimeHelper';

type BoundingBox = {
  north: number;
  south: number;
  east: number;
  west: number;
};

interface MapLocation {
  name: string;
  displayName: string;
  description: string;
  latitude: number;
  longitude: number;
  zoom: number;
  type: string;
  boundingBox: BoundingBox;
}

interface ScheduleInfo {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  durationDays: number | null;
}

interface ReviewPayload {
  editMode?: boolean;
  ruleId?: string;
  forceAppend?: boolean;
  originalRule?: any;
  ruleName: string;
  redirectUrl: string;
  priority: string;
  selectedCriteria: string[];
  qrCodes: string[];
  styleCodes: string[];
  styleCodesNewList: string[];
  selectedCountries: string[];
  selectedDivision: string;
  selectedCategory: string;
  brandName: string;
  athleteFullName: string;
  gtinCodes: string[];
  selectedLocation: MapLocation | null;
  selectedBoundingBox: BoundingBox | null;
  schedule: ScheduleInfo;
}

const formatDateTimeLabel = (date?: string, time?: string) => {
  if (!date) return '--';
  const safeTime = time || '00:00';
  
  // Parse date and time without timezone conversion
  // User enters time in their local timezone, so don't treat it as UTC
  const composed = `${date}T${safeTime}:00`;
  const dt = new Date(composed);
  if (Number.isNaN(dt.getTime())) {
    return '--';
  }
  
  // Format date as DD/MM/YYYY
  const day = dt.getDate().toString().padStart(2, '0');
  const month = (dt.getMonth() + 1).toString().padStart(2, '0');
  const year = dt.getFullYear();
  const formattedDate = `${day}/${month}/${year}`;
  
  // Format time as 12-hour with AM/PM
  let hours = dt.getHours();
  const minutes = dt.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const formattedTime = `${hours}:${minutes} ${ampm}`;
  
  return `${formattedDate} ${formattedTime} IST`;
};

interface SectionProps {
  title: string;
  accentColor?: string;
  background?: string;
  borderColor?: string;
}

const Section: React.FC<React.PropsWithChildren<SectionProps>> = ({
  title,
  children,
  accentColor = '#000',
  background = '#fafafa',
  borderColor = '#ddd'
}) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 2,
      border: `1px solid ${borderColor}`,
      background,
      color: '#000',
      p: 3,
      fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
    }}
  >
    <Typography
      variant="h6"
      sx={{
        pb: 1,
        mb: 2,
        fontSize: 18,
        fontWeight: 700,
        color: accentColor,
        fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
      }}
    >
      {title}
    </Typography>
    {children}
  </Paper>
);

const SummaryRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 2, py: 0.5 }}>
    <Typography sx={{ minWidth: 150, color: '#666', fontSize: 16, fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif" }}>{label}</Typography>
    <Typography sx={{ color: '#000', fontWeight: 500, fontSize: 16, fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif" }}>{value || '--'}</Typography>
  </Box>
);

const ReviewRule: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const data = (location.state as ReviewPayload | undefined) || null;
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [pendingChangesInfo, setPendingChangesInfo] = useState<any>(null);
  const [updatePayload, setUpdatePayload] = useState<any>(null);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Helper to format email to name
  const formatCreatorName = (email: string) => {
    if (!email || email === 'Unknown') return 'Unknown';
    const username = email.split('@')[0];
    const nameParts = username.split('.');
    return nameParts.map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  };

  const countryNames = useMemo(() => {
    if (!data?.selectedCountries?.length) return '--';
    return data.selectedCountries
      .map((code) => COUNTRY_OPTIONS.find((c) => c.code === code)?.name || code)
      .join(', ');
  }, [data]);

  if (!data) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9', py: 6 }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              No rule data available
            </Typography>
            <Typography sx={{ color: '#6b7280', mb: 3 }}>
              Please complete the Create Rule form before reviewing the rule summary.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/create-rule')} sx={{ textTransform: 'none', background: '#2196F3' }}>
              Go to Create Rule
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  const matchingRows: { label: string; value: React.ReactNode }[] = [];
  if (data.selectedCriteria.includes('qrCodes')) {
    matchingRows.push({ label: 'QR Codes', value: data.qrCodes.length ? data.qrCodes.join(', ') : 'None added' });
  }
  if (data.selectedCriteria.includes('styleColor')) {
    matchingRows.push({
      label: 'Style Colour',
      value: data.styleCodes.length ? data.styleCodes.join(', ') : 'None added'
    });
  }
  if (data.selectedCriteria.includes('styleCodesNew')) {
    matchingRows.push({
      label: 'Style Codes',
      value: data.styleCodesNewList.length ? data.styleCodesNewList.join(', ') : 'None added'
    });
  }
  if (data.selectedCriteria.includes('country')) {
    matchingRows.push({ label: 'Country', value: countryNames });
  }
  if (data.selectedCriteria.includes('division')) {
    matchingRows.push({ label: 'Division', value: data.selectedDivision || '--' });
  }
  if (data.selectedCriteria.includes('brandName')) {
    matchingRows.push({ label: 'Brand Name', value: data.brandName || '--' });
  }
  if (data.selectedCriteria.includes('athleteFullName')) {
    matchingRows.push({ label: 'Athlete', value: data.athleteFullName || '--' });
  }
  if (data.selectedCriteria.includes('gtins')) {
    matchingRows.push({
      label: 'GTINs',
      value: data.gtinCodes.length ? data.gtinCodes.join(', ') : 'None added'
    });
  }
  if (data.selectedCriteria.includes('category')) {
    matchingRows.push({ label: 'Category', value: data.selectedCategory || '--' });
  }
  if (data.selectedCriteria.includes('boundingBox')) {
    const bbox = data.selectedBoundingBox || data.selectedLocation?.boundingBox || null;
    if (bbox) {
      // Only show coordinates that are not zero (not set by user)
      const coordinates = [
        bbox.north !== 0 ? `North: ${bbox.north.toFixed(4)}` : null,
        bbox.south !== 0 ? `South: ${bbox.south.toFixed(4)}` : null,
        bbox.east !== 0 ? `East: ${bbox.east.toFixed(4)}` : null,
        bbox.west !== 0 ? `West: ${bbox.west.toFixed(4)}` : null
      ].filter(Boolean);
      
      const bboxValue = coordinates.length > 0 ? coordinates.join(', ') : 'No coordinates entered';
      matchingRows.push({ label: 'Bounding Box', value: bboxValue });
    } else {
      matchingRows.push({ label: 'Bounding Box', value: 'No coordinates captured' });
    }
  }

  const scheduleStart = formatDateTimeLabel(data.schedule.startDate, data.schedule.startTime);
  const scheduleEnd = formatDateTimeLabel(data.schedule.endDate, data.schedule.endTime);

  const handleForceAppend = async () => {
    if (!data.ruleId || !updatePayload) return;
    
    setConflictDialogOpen(false);
    setSubmitting(true);
    
    try {
      // Retry with forceAppend: true
      const updateResult = await updateRule(data.ruleId, {
        ...updatePayload,
        forceAppend: true
      });
      
      setSuccessMessage(updateResult.message || 'Changes merged successfully!');
      setShowSuccessNotification(true);
    } catch (error: any) {
      setErrorMessage(`Error: ${error.message}`);
      setShowErrorNotification(true);
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!confirmed) {
      setErrorMessage('Please confirm the rule details before submitting.');
      setShowErrorNotification(true);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Map frontend data to backend schema
      const priorityMap: { [key: string]: number } = {
        'low': 80,
        'medium': 100,
        'high': 110,
        'critical': 124
      };
      
      // Combine start date and time for ruleStartFrom and convert to UTC
      const ruleStartFrom = combineDateTimeToUTC(data.schedule?.startDate, data.schedule?.startTime);
      
      // Combine end date and time for ruleEndAt and convert to UTC
      const ruleEndAt = combineDateTimeToUTC(data.schedule?.endDate, data.schedule?.endTime);

      const ruleInput: any = {
        name: data.ruleName,
        redirectUrl: data.redirectUrl,
        priority: priorityMap[data.priority]
      };
      
      // Only add schedule fields if they have values
      if (ruleStartFrom) ruleInput.ruleStartFrom = ruleStartFrom;
      if (ruleEndAt) ruleInput.ruleEndAt = ruleEndAt;

      // Add optional fields — include values if present, or explicit empty if clearing in edit mode
      const originalRule = data.editMode ? data.originalRule : null;

      if (data.brandName) {
        ruleInput.brandName = data.brandName;
      } else if (originalRule?.brandName) {
        ruleInput.brandName = '';
      }

      if (data.athleteFullName) {
        ruleInput.athleteFullName = data.athleteFullName;
      } else if (originalRule?.athleteFullName) {
        ruleInput.athleteFullName = '';
      }

      if (data.qrCodes && data.qrCodes.length > 0) {
        ruleInput.qrs = data.qrCodes;
      } else if (originalRule?.qrs?.length) {
        ruleInput.qrs = [];
      }

      if (data.styleCodes && data.styleCodes.length > 0) {
        ruleInput.styleColorCodes = data.styleCodes;
      } else if (originalRule?.styleColorCodes?.length) {
        ruleInput.styleColorCodes = [];
      }

      if (data.styleCodesNewList && data.styleCodesNewList.length > 0) {
        ruleInput.styleCodes = data.styleCodesNewList;
      } else if (originalRule?.styleCodes?.length) {
        ruleInput.styleCodes = [];
      }

      if (data.gtinCodes && data.gtinCodes.length > 0) {
        ruleInput.gtins = data.gtinCodes;
      } else if (originalRule?.gtins?.length) {
        ruleInput.gtins = [];
      }

      if (data.selectedCountries && data.selectedCountries.length > 0) {
        ruleInput.countries = data.selectedCountries;
      } else if (originalRule?.countries?.length) {
        ruleInput.countries = [];
      }

      if (data.selectedDivision) {
        ruleInput.divisions = [data.selectedDivision];
      } else if (originalRule?.divisions?.length) {
        ruleInput.divisions = [];
      }

      if (data.selectedCategory) {
        ruleInput.categories = [data.selectedCategory];
      } else if (originalRule?.categories?.length) {
        ruleInput.categories = [];
      }

      if (data.selectedBoundingBox || data.selectedLocation?.boundingBox) {
        ruleInput.boundingBox = data.selectedBoundingBox || data.selectedLocation?.boundingBox;
      } else if (originalRule?.boundingBox) {
        ruleInput.boundingBox = null;
      }

      // Add forceAppend if merging with pending changes
      if (data.forceAppend) {
        ruleInput.forceAppend = true;
      }

      if (data.editMode && data.ruleId) {
        // Update existing rule - send only changed fields
        const updateInput: any = {};
        
        // Helper function to compare values
        const hasChanged = (newVal: any, oldVal: any, fieldName?: string) => {
          // Both are null/undefined - not changed
          if ((newVal === null || newVal === undefined) && (oldVal === null || oldVal === undefined)) {
            return false;
          }
          // One is null/undefined, other isn't - changed
          if ((newVal === null || newVal === undefined) || (oldVal === null || oldVal === undefined)) {
            return true;
          }
          // Special handling for date fields - compare only date+time, ignore timezone
          if (fieldName === 'ruleStartFrom' || fieldName === 'ruleEndAt') {
            try {
              const oldDate = new Date(oldVal);
              const newDate = new Date(newVal);
              
              // Compare year, month, day, hour, minute (ignore seconds, ms)
              const oldDateStr = `${oldDate.getFullYear()}-${String(oldDate.getMonth() + 1).padStart(2, '0')}-${String(oldDate.getDate()).padStart(2, '0')} ${String(oldDate.getHours()).padStart(2, '0')}:${String(oldDate.getMinutes()).padStart(2, '0')}`;
              const newDateStr = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}-${String(newDate.getDate()).padStart(2, '0')} ${String(newDate.getHours()).padStart(2, '0')}:${String(newDate.getMinutes()).padStart(2, '0')}`;
              
              // If date+time are the same, not changed
              if (!isNaN(oldDate.getTime()) && !isNaN(newDate.getTime())) {
                return oldDateStr !== newDateStr;
              }
            } catch (e) {
              // Fall through to string comparison if date parsing fails
            }
          }
          // Compare arrays
          if (Array.isArray(newVal) && Array.isArray(oldVal)) {
            if (newVal.length !== oldVal.length) return true;
            return JSON.stringify(newVal.sort()) !== JSON.stringify(oldVal.sort());
          }
          // Compare objects
          if (typeof newVal === 'object' && newVal !== null && typeof oldVal === 'object' && oldVal !== null) {
            return JSON.stringify(newVal) !== JSON.stringify(oldVal);
          }
          // Compare primitive values (including strings)
          // Normalize strings by trimming whitespace
          if (typeof newVal === 'string' && typeof oldVal === 'string') {
            return newVal.trim() !== oldVal.trim();
          }
          return newVal !== oldVal;
        };
        
        // Only include fields that changed
        if (hasChanged(ruleInput.name, originalRule?.name)) updateInput.name = ruleInput.name;
        if (hasChanged(ruleInput.redirectUrl, originalRule?.redirectUrl)) updateInput.redirectUrl = ruleInput.redirectUrl;
        if (hasChanged(ruleInput.priority, originalRule?.priority)) updateInput.priority = ruleInput.priority;
        
        // For schedule fields, only include if they exist AND changed
        if (ruleInput.ruleStartFrom) {
          if (hasChanged(ruleInput.ruleStartFrom, originalRule?.ruleStartFrom, 'ruleStartFrom')) {
            updateInput.ruleStartFrom = ruleInput.ruleStartFrom;
          }
        }
        if (ruleInput.ruleEndAt) {
          if (hasChanged(ruleInput.ruleEndAt, originalRule?.ruleEndAt, 'ruleEndAt')) {
            updateInput.ruleEndAt = ruleInput.ruleEndAt;
          }
        }
        
        if (ruleInput.brandName !== undefined && hasChanged(ruleInput.brandName, originalRule?.brandName)) updateInput.brandName = ruleInput.brandName;
        if (ruleInput.athleteFullName !== undefined && hasChanged(ruleInput.athleteFullName, originalRule?.athleteFullName)) updateInput.athleteFullName = ruleInput.athleteFullName;
        if (ruleInput.qrs !== undefined && hasChanged(ruleInput.qrs, originalRule?.qrs)) updateInput.qrs = ruleInput.qrs;
        if (ruleInput.styleColorCodes !== undefined && hasChanged(ruleInput.styleColorCodes, originalRule?.styleColorCodes)) updateInput.styleColorCodes = ruleInput.styleColorCodes;
        if (ruleInput.styleCodes !== undefined && hasChanged(ruleInput.styleCodes, originalRule?.styleCodes)) updateInput.styleCodes = ruleInput.styleCodes;
        if (ruleInput.gtins !== undefined && hasChanged(ruleInput.gtins, originalRule?.gtins)) updateInput.gtins = ruleInput.gtins;
        if (ruleInput.countries !== undefined && hasChanged(ruleInput.countries, originalRule?.countries)) updateInput.countries = ruleInput.countries;
        if (ruleInput.divisions !== undefined && hasChanged(ruleInput.divisions, originalRule?.divisions)) updateInput.divisions = ruleInput.divisions;
        if (ruleInput.categories !== undefined && hasChanged(ruleInput.categories, originalRule?.categories)) updateInput.categories = ruleInput.categories;
        if (ruleInput.boundingBox !== undefined && hasChanged(ruleInput.boundingBox, originalRule?.boundingBox)) updateInput.boundingBox = ruleInput.boundingBox;
        
        // Add forceAppend if merging with pending changes
        if (data.forceAppend) {
          updateInput.forceAppend = true;
        }

        // Store payload for potential retry with forceAppend
        setUpdatePayload(updateInput);
        
        try {
          const updateResult = await updateRule(data.ruleId, updateInput);

          // Check if there are pending changes conflict
          if (!updateResult.success && updateResult.pendingChangesInfo) {
            // Has pending changes - show conflict dialog
            setPendingChangesInfo(updateResult.pendingChangesInfo);
            setConflictDialogOpen(true);
            setSubmitting(false);
            return; // Don't proceed further
          }
          
          // Success - no pending changes
          const successMsg = updateResult.message || 'Rule updated successfully!';
          setSuccessMessage(successMsg);
          setShowSuccessNotification(true);
        } catch (updateError: any) {
          // Check if this is a pending changes error
          if (updateError.message && updateError.message.includes('pending changes')) {
            // Try to get pending changes info from enhanced error or graphQL errors
            let pendingInfo = updateError.pendingChangesInfo;
            
            // If not in error object, try to fetch the rule to get its pendingChanges
            if (!pendingInfo && data.ruleId) {
              try {
                // Fetch the specific rule to get current pending changes
                const currentRule = await getRule(data.ruleId);

                if (currentRule?.pendingChanges) {
                  pendingInfo = {
                    operationType: currentRule.pendingChanges.operationType,
                    requestedBy: currentRule.pendingChanges.requestedBy,
                    requestedAt: currentRule.pendingChanges.requestedAt,
                    changes: currentRule.pendingChanges.changes
                  };

                }
              } catch (fetchError) {
                console.error('Could not fetch current rule:', fetchError);
              }
            }
            
            setPendingChangesInfo(pendingInfo || {
              operationType: 'UPDATE',
              requestedBy: 'Unknown',
              requestedAt: new Date().toISOString(),
              changes: null
            });
            setConflictDialogOpen(true);
            setSubmitting(false);
            return;
          }
          // If it's another error, rethrow
          throw updateError;
        }
      } else {
        // Create new rule
        const createdRule = await createRule(ruleInput);

        // Show success notification
        const message = createdRule.message || 'Rule created successfully with pending approval status!';
        setSuccessMessage(message);
        setShowSuccessNotification(true);
      }
    } catch (err: any) {
      const action = data.editMode ? 'update' : 'create';
      console.error(`Error ${action}ing rule:`, err);
      setError(err.message || `Failed to ${action} rule. Please try again.`);
      setErrorMessage(`Error: ${err.message || `Failed to ${action} rule`}`);
      setShowErrorNotification(true);
    } finally {
      setSubmitting(false);
    }
  };

  const shouldShowMatchingSection = matchingRows.length > 0;

  return (
    <Box sx={{ minHeight: '100vh', py: 6, color: '#000', bgcolor: '#ffffff' }}>
      <Container maxWidth="md">
        <Stack spacing={3}>
          <Box sx={{ width: '80%', mx: 'auto', mb: 2 }}>
            <Typography 
              sx={{ 
                fontFamily: "'Impact', 'Arial Black', sans-serif",
                fontWeight: 700,
                color: '#000',
                fontSize: '28px',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                textAlign: 'left',
                mb: 0
              }}
            >
              {data.editMode ? 'REVIEW AND UPDATE RULE' : 'REVIEW RULE'}
            </Typography>
          </Box>

          <Section title="Basic Information">
            <SummaryRow label="Rule Name" value={data.ruleName || '--'} />
            <SummaryRow label="Redirect URL" value={data.redirectUrl || '--'} />
            <SummaryRow label="Priority" value={data.priority || '--'} />
          </Section>

          {shouldShowMatchingSection && (
            <Section title="Matching Criteria">
              {matchingRows.map((row) => (
                <SummaryRow key={row.label} label={row.label} value={row.value} />
              ))}
            </Section>
          )}

          <Section title="Timing and Schedule">
            <SummaryRow label="Start" value={scheduleStart} />
            <SummaryRow label="End" value={scheduleEnd} />
            <SummaryRow
              label="Duration"
              value={
                data.schedule.durationDays !== null
                  ? `${data.schedule.durationDays} day${data.schedule.durationDays === 1 ? '' : 's'}`
                  : '--'
              }
            />
          </Section>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid #ddd',
              background: '#fafafa',
              p: 3
            }}
          >
            {error && (
              <Box sx={{ mb: 2, p: 2, bgcolor: '#fee2e2', borderRadius: 1 }}>
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              </Box>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  disabled={submitting}
                  sx={{ color: '#2196F3', '&.Mui-checked': { color: '#2196F3' } }}
                />
              }
              label="I confirm the rule details are correct"
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                variant="outlined"
                onClick={() =>
                  navigate('/create-rule', {
                    state: {
                      mode: data.editMode ? 'edit' : 'create',
                      ruleData: data.originalRule,
                      forceAppend: data.forceAppend,
                      selectedCriteria: data.selectedCriteria,
                      formData: data
                    }
                  })
                }
                disabled={submitting}
                sx={{ 
                  textTransform: 'none', 
                  flex: 1,
                  borderColor: '#2196F3',
                  color: '#2196F3',
                  '&:hover': {
                    borderColor: '#1976D2',
                    background: 'rgba(33, 150, 243, 0.08)',
                    color: '#1976D2'
                  }
                }}
              >
                Back
              </Button>
              <Button
                startIcon={submitting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                variant="contained"
                onClick={handleSubmit}
                disabled={!confirmed || submitting}
                sx={{ 
                  textTransform: 'none', 
                  flex: 1,
                  background: '#2196F3',
                  '&:hover': {
                    background: '#1976D2'
                  },
                  '&.Mui-disabled': {
                    background: '#ccc',
                    color: '#888'
                  }
                }}
                >
                {submitting ? (data.editMode ? 'Updating...' : 'Submitting...') : (data.editMode ? 'Update Rule' : 'Submit Rule')}
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </Container>

      <Dialog
        open={conflictDialogOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return;
          setConflictDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon sx={{ color: '#ff9800', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Pending Changes Detected
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body1">
              This rule already has pending changes awaiting approval:
            </Typography>
            
            {pendingChangesInfo && (
              <>
                <Box sx={{ bgcolor: '#fff3e0', p: 2, borderRadius: 1, border: '1px solid #ffb74d' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#000' }}>
                    Operation Type
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {pendingChangesInfo.operationType}
                  </Typography>
                  
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#000' }}>
                  Requested By
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {formatCreatorName(pendingChangesInfo.requestedBy)}
                </Typography>
                  
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#000' }}>
                    Requested At
                  </Typography>
                  <Typography variant="body2">
                    {new Date(pendingChangesInfo.requestedAt).toLocaleString()}
                  </Typography>
                </Box>
                
                {/* Display Changes if available */}
                {pendingChangesInfo.changes && (() => {

                  return (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: '#000' }}>
                        Modified Fields:
                      </Typography>
                      <Box sx={{ bgcolor: '#fff', p: 1.5, borderRadius: 1, border: '1px solid #e0e0e0' }}>
                        {(() => {
                          try {
                            const changes = typeof pendingChangesInfo.changes === 'string' 
                              ? JSON.parse(pendingChangesInfo.changes) 
                              : pendingChangesInfo.changes;

                            if (!changes || Object.keys(changes).length === 0) {
                              return (
                                <Typography variant="body2" sx={{ fontSize: '13px', fontStyle: 'italic', color: '#666' }}>
                                  No field changes found
                                </Typography>
                              );
                            }
                            
                            return Object.entries(changes).map(([key, value]: [string, any]) => {
                              // Format the value display
                              let displayValue: string;
                              
                              // Handle nested structure with "new" property { fieldName: { new: [...] } }
                              let actualValue = value;
                              if (typeof value === 'object' && value !== null && value.new !== undefined) {
                                actualValue = value.new;
                              }
                              
                              // Special handling for countries - convert codes to full names
                              if (key === 'countries' && Array.isArray(actualValue)) {
                                displayValue = actualValue
                                  .map((code: string) => {
                                    const country = COUNTRY_OPTIONS.find(c => c.code === code);
                                    return country ? country.name : code;
                                  })
                                  .join(', ');
                              } else if (Array.isArray(actualValue)) {
                                displayValue = actualValue.join(', ');
                              } else if (typeof actualValue === 'object' && actualValue !== null) {
                                // If still an object, stringify it
                                displayValue = JSON.stringify(actualValue);
                              } else {
                                displayValue = String(actualValue);
                              }
                              
                              return (
                                <Box key={key} sx={{ mb: 1, '&:last-child': { mb: 0 } }}>
                                  <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 600, color: '#666' }}>
                                    {key}:
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 400, color: '#000', ml: 1 }}>
                                    {displayValue}
                                  </Typography>
                                </Box>
                              );
                            });
                          } catch (e) {
                            console.error('Error parsing changes:', e);
                            return (
                              <Typography variant="body2" sx={{ fontSize: '13px', fontStyle: 'italic', color: '#666' }}>
                                Unable to parse changes: {String(e)}
                              </Typography>
                            );
                          }
                        })()}
                      </Box>
                    </Box>
                  );
                })()}
              </>
            )}
            
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#d32f2f' }}>
              Would you like to merge your new changes with the pending changes?
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
              Choosing "Merge" will append your changes to the existing pending changes.
              Both sets of changes will need approval.
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2, gap: 1 }}>
          <Button 
            onClick={() => setConflictDialogOpen(false)}
            variant="outlined"
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleForceAppend}
            variant="contained"
            disabled={submitting}
            sx={{
              backgroundColor: '#ff9800',
              '&:hover': {
                backgroundColor: '#f57c00'
              }
            }}
          >
            {submitting ? 'Merging...' : 'Merge Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Notification */}
      <SuccessNotification
        open={showSuccessNotification}
        title="Congratulations!"
        message={successMessage}
        onClose={() => {
          setShowSuccessNotification(false);
          navigate('/', { state: { view: 'my-rules' } });
        }}
      />

      {/* Inline notification (keeping for now) */}
      {false && showSuccessNotification && (
        <Box
          sx={{
            position: 'fixed',
            top: '20%',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            width: '90%',
            maxWidth: '500px',
            animation: 'slideDown 0.3s ease-out',
            '@keyframes slideDown': {
              '0%': { opacity: 0, transform: 'translate(-50%, -20px)' },
              '100%': { opacity: 1, transform: 'translate(-50%, 0)' }
            }
          }}
        >
          <Paper
            elevation={8}
            sx={{
              backgroundColor: '#d4edda',
              border: '1px solid #c3e6cb',
              borderRadius: '12px',
              p: 3,
              position: 'relative',
              '@keyframes progressBar': {
                '0%': { width: '0%' },
                '100%': { width: '100%' }
              }
            }}
          >
            {/* Close Button */}
            <IconButton
              onClick={() => {
                setShowSuccessNotification(false);
                navigate('/', { state: { view: 'my-rules' } });
              }}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: '#155724',
                '&:hover': {
                  backgroundColor: 'rgba(21, 87, 36, 0.1)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Content */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Green Checkmark Badge */}
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  backgroundColor: '#28a745',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <CheckCircleIcon sx={{ color: '#fff', fontSize: 40 }} />
              </Box>

              {/* Text Content */}
              <Box sx={{ flex: 1, pr: 3 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#155724',
                    mb: 0.5,
                    fontSize: 22
                  }}
                >
                  Congratulations!
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#155724',
                    fontSize: 15
                  }}
                >
                  {successMessage}
                </Typography>
              </Box>
            </Box>

            {/* OK Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="contained"
                onClick={() => {
                  setShowSuccessNotification(false);
                  navigate('/');
                }}
                sx={{
                  backgroundColor: '#28a745',
                  color: '#fff',
                  fontWeight: 600,
                  px: 5,
                  py: 1.2,
                  textTransform: 'none',
                  fontSize: 16,
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: '#218838'
                  }
                }}
              >
                OK
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Error Notification */}
      <ErrorNotification
        open={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
    </Box>
  );
};

export default ReviewRule;
